import { useCallback, useMemo, useRef, useState } from 'react';
import { isEmpty, uniq } from 'lodash';
import useFabricFavs from 'data-hooks/useFabricFavs';
import usePDPOrderEdit from 'data-hooks/usePDPOrderEdit';
import useFlashSaleList from 'hooks/useFlashSaleList';
import usePDPOptionsClearanceData from 'data-hooks/useClearanceData/usePDPOptionsClearanceData';
import parseFabricMaterial from 'commons/parseFabricMaterial';
import { PDP_READYTOSHIP_TAB_ID, PDP_NAMED_FILTERS, GREENGUARD_FABRICS, PANTONE_FABRIC_CONFIG } from 'commons/constants';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import trimPopularMaterialsArray from './trimPopularMaterialsArray';
import moveMaterialToStart from './moveMaterialToStart';
import getUniqueMaterials from './getUniqueMaterials';
const {
  pantoneFilterSlug
} = PANTONE_FABRIC_CONFIG;
const usePDPFabricFilters = (slug, isEditOrder) => {
  const {
    data: {
      options,
      productId,
      selectedOptions,
      selectedOptionsFromURLOverride,
      stock,
      upholsteryOptionId
    } = {}
  } = usePDPOptions();
  const {
    data: {
      userFabricFavs: favMaterialsObj,
      fabricFavsInfo
    } = {}
  } = useFabricFavs({
    loadMaterialDetails: true
  });
  const {
    products: flashSaleProductIds,
    variations: flashSaleVariations
  } = useFlashSaleList();
  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();
  const [activeNamedFilters, setActiveNamedFilters] = useState([]);
  const resetNamedFilters = useCallback(() => {
    setActiveNamedFilters([]);
  }, []);
  const [activeColorFilters, setActiveColorFilters] = useState([]);
  const resetColorFilters = useCallback(() => {
    setActiveColorFilters([]);
  }, []);
  const clearAllFilters = useCallback(() => {
    resetNamedFilters();
    resetColorFilters();
  }, [resetColorFilters, resetNamedFilters]);

  // Toggle named upholstery filters such as Velvets, Safeguard
  const toggleNamedFilter = useCallback(filterId => {
    setActiveNamedFilters(prev => prev?.includes(filterId) ? prev?.filter(v => v !== filterId) : [...prev, filterId]);
  }, []);
  const toggleColorFilter = useCallback(color => {
    setActiveColorFilters(prev_0 => {
      const updatedActiveColorFilters = prev_0?.includes(color) ? prev_0?.filter(v_0 => v_0 !== color) : [...prev_0, color];
      return uniq(updatedActiveColorFilters);
    });
  }, []);
  const {
    data: orderEditData = {}
  } = usePDPOrderEdit({
    skip: !isEditOrder
  });

  // for clearance materials
  const {
    data: productClearanceData
  } = usePDPOptionsClearanceData(productId);
  const editOrderDiscount = orderEditData?.cartItem?.discount_percent; // this can become problematic in some edge cases
  const formatOptionValue = useCallback(({
    additional_cost: additionalCost,
    color: color_0,
    description,
    exclude_from_sale: excludeFromSale,
    group,
    id,
    image,
    is_active: isActive,
    performance_fabric: performanceFabric,
    pet_friendly: petFriendly,
    pantone_flag,
    popular,
    popularOrder,
    sku,
    sprite_config: spriteConfig,
    sustainable_fabric: sustainableFabric,
    tier,
    grade,
    value: label
  }) => {
    const groupName = group?.name || '';
    const materials = parseFabricMaterial(group?.material);
    const velvet = materials.includes('velvet');
    const greenguardFabric = materials.includes('greenguard') || materials.includes('greenguard-gold') || (groupName ? GREENGUARD_FABRICS.includes(groupName) : false);
    return {
      additionalCost: isEditOrder && !excludeFromSale && additionalCost > 0 && editOrderDiscount > 0 ? Math.round(additionalCost * ((100 - editOrderDiscount) / 100)) : additionalCost,
      cleaningCode: group?.cleaning_code,
      color: color_0,
      description,
      exclude_from_sale: excludeFromSale,
      greenguard_fabric: greenguardFabric,
      groupMaterial: group?.material,
      groupMaterialDesc: group?.material_description,
      groupName,
      image,
      is_active: isActive,
      label,
      materialId: id,
      origAddlCost: additionalCost,
      performance_fabric: performanceFabric,
      pet_friendly: petFriendly,
      pantone_flag,
      popular,
      popularOrder,
      sku,
      spriteConfig,
      sustainable_fabric: sustainableFabric,
      tier,
      grade,
      upholsteryOptionId,
      velvet
    };
  }, [editOrderDiscount, isEditOrder, upholsteryOptionId]);

  // optimize calls
  const clearanceFabricsSKU = useMemo(() => Object.keys(productClearanceData?.optionsClearanceData || {}), [productClearanceData?.optionsClearanceData]);
  const isReadyToShipTab = pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID;
  const finalOptions = isReadyToShipTab ? stock?.stockOptions : options;
  const upholsteryOption = finalOptions?.find(({
    id: id_0
  }) => id_0 === upholsteryOptionId);
  const upholsteryOptionTypeName = upholsteryOption?.name || 'upholstery';
  const allMaterialsForCurrentTab = useMemo(() => {
    if (!upholsteryOption) {
      return [];
    }
    return (upholsteryOption?.values || []).filter(optionValue => !isReadyToShipTab && optionValue?.is_active || isReadyToShipTab).map(optionValue_0 => formatOptionValue(optionValue_0)).map(material => {
      if (material.pantone_flag === 1) {
        return {
          ...material,
          [pantoneFilterSlug]: true
        };
      }
      return material;
    }).sort((a, b) => a.label.localeCompare(b.label));
  }, [formatOptionValue, isReadyToShipTab, upholsteryOption]);
  const favoriteMaterials = useMemo(() => {
    if (!allMaterialsForCurrentTab) {
      return [];
    }
    const allFavoriteMaterials = allMaterialsForCurrentTab.filter(material_0 => favMaterialsObj?.[material_0.materialId]);
    const favMaterialIds = allFavoriteMaterials.reduce((acc, material_1) => ({
      ...acc,
      [material_1.materialId]: true
    }), {});
    const updatedFavoriteMaterials = [...allFavoriteMaterials];

    // CON-3009 - Get details of fav fabrics not applicable to product
    const favMaterialsNotApplicableToProduct = Object.keys(favMaterialsObj || {}).filter(materialId => !favMaterialIds[materialId]);
    if (favMaterialsNotApplicableToProduct?.length > 0 && !isEmpty(fabricFavsInfo)) {
      favMaterialsNotApplicableToProduct.forEach(materialId_0 => {
        const materialDetail = fabricFavsInfo[materialId_0];
        if (materialDetail) {
          updatedFavoriteMaterials.push({
            ...materialDetail,
            applicableForProduct: false,
            label: materialDetail.value?.trim(),
            groupMaterial: materialDetail.group?.material,
            groupMaterialDesc: materialDetail.group?.material_description,
            cleaningCode: materialDetail.group?.cleaning_code,
            pet_friendly: materialDetail.group?.pet_friendly,
            pantone_flag: materialDetail.group?.pantone_flag,
            velvet: materialDetail.group?.material === 'velvet',
            greenguard_fabric: materialDetail.group?.material === 'greenguard' || GREENGUARD_FABRICS.includes(materialDetail.group?.name),
            materialId: parseInt(materialId_0, 10)
          });
        }
      });
    }
    return updatedFavoriteMaterials;
  }, [allMaterialsForCurrentTab, fabricFavsInfo, favMaterialsObj]);
  const allInStockValues = useMemo(() => {
    if (!stock?.stockOptions?.length || !upholsteryOptionId) {
      return null;
    }

    // Find first in stock value
    const inStockOption = stock?.stockOptions.find(({
      id: id_1
    }) => id_1 === upholsteryOptionId);
    const updatedAllInStockValues = inStockOption ? inStockOption.values : null;
    return updatedAllInStockValues;
  }, [stock?.stockOptions, upholsteryOptionId]);
  const instockOptionValueIds = useMemo(() => {
    const updatedInstockOptionValueIds = [];
    allInStockValues?.forEach(instockVal => {
      if (instockVal?.is_active) {
        updatedInstockOptionValueIds.push(instockVal.id);
      }
    });
    return updatedInstockOptionValueIds;
  }, [allInStockValues]);
  const inStockOptions = useMemo(() => {
    const updatedInStockOptions = allInStockValues ? allInStockValues.map(instockVal_0 => {
      if (!instockVal_0?.is_active) {
        return null;
      }
      return {
        materialId: instockVal_0.id,
        label: instockVal_0.value,
        image: instockVal_0.image,
        spriteConfig: instockVal_0.sprite_config,
        upholsteryOptionId,
        description: instockVal_0.description,
        groupName: instockVal_0.group?.name || '',
        tier: instockVal_0.tier,
        color: instockVal_0.color,
        popular: instockVal_0.popular,
        pet_friendly: instockVal_0.pet_friendly,
        pantone_flag: instockVal_0.pantone_flag,
        exclude_from_sale: instockVal_0.exclude_from_sale,
        performance_fabric: instockVal_0.performance_fabric,
        sustainable_fabric: instockVal_0.sustainable_fabric,
        sku: instockVal_0.sku,
        additionalCost: isEditOrder && !instockVal_0.exclude_from_sale && instockVal_0.additional_cost > 0 && editOrderDiscount > 0 ? Math.round(instockVal_0.additional_cost * ((100 - editOrderDiscount) / 100)) : instockVal_0.additional_cost,
        groupMaterial: instockVal_0.group?.material,
        groupMaterialDesc: instockVal_0.group?.material_description,
        cleaningCode: instockVal_0.group?.cleaning_code,
        origAddlCost: instockVal_0.additional_cost
      };
    }).filter(v_1 => !!v_1) : null;
    return updatedInStockOptions;
  }, [allInStockValues, editOrderDiscount, isEditOrder, upholsteryOptionId]);

  // Find selected material
  const selectedMaterial = useMemo(() => {
    if (!selectedOptions) return {};
    const selectedMaterialId = selectedOptions[upholsteryOptionId];
    const updatedSelectedMaterial = allMaterialsForCurrentTab.find(({
      materialId: materialId_1
    }) => materialId_1 === selectedMaterialId);
    return updatedSelectedMaterial || {};
  }, [allMaterialsForCurrentTab, selectedOptions, upholsteryOptionId]);

  // Set the initial selected material using useRef so that it is only set
  // during first render.
  const initialSelectedMaterialRef = useRef(selectedMaterial);
  const isProductInFlashSale = flashSaleProductIds?.includes(productId);
  const flashSaleVariation = useMemo(() => isProductInFlashSale ? flashSaleVariations?.[productId] : {}, [flashSaleVariations, isProductInFlashSale, productId]);
  const flashSaleMaterials = useMemo(() => isProductInFlashSale ? allMaterialsForCurrentTab.filter(val => flashSaleVariation[upholsteryOptionId]?.includes(val.materialId)) : [], [allMaterialsForCurrentTab, flashSaleVariation, isProductInFlashSale, upholsteryOptionId]);

  // Derive all materials marked as popular in API response
  const allPopularMaterials = useMemo(() => {
    // Find all materials marked as popular that are not part of the flash
    // sale, and sort them by popularOrder
    const allPopularMaterialsInOrder = allMaterialsForCurrentTab.filter(material_2 => material_2.popular && !flashSaleVariation[upholsteryOptionId]?.includes(material_2.materialId)).sort((a_0, b_0) => a_0.popularOrder - b_0.popularOrder);

    // If there are no materials marked as popular, then use all materials
    return allPopularMaterialsInOrder.length ? allPopularMaterialsInOrder : allMaterialsForCurrentTab;
  }, [allMaterialsForCurrentTab, flashSaleVariation, upholsteryOptionId]);

  // Show any flash sale materials first in the popular materials group
  const popularMaterialsFlashSalesFirst = useMemo(() => {
    const allPopularMaterialsWithFlashSales = [...flashSaleMaterials, ...allPopularMaterials];
    return trimPopularMaterialsArray(allPopularMaterialsWithFlashSales);
  }, [allPopularMaterials, flashSaleMaterials]);

  // Show flash sales and in stock materials first in the popular group
  const popularMaterialsFlashSalesAndInStockFirst = useMemo(() => {
    const allStockOptionsPresent = !!inStockOptions?.length && !selectedOptionsFromURLOverride && !!inStockOptions.every((stockedOption, idx) => popularMaterialsFlashSalesFirst?.[idx] === stockedOption.materialId);
    if (inStockOptions?.length && !selectedOptionsFromURLOverride) {
      if (allStockOptionsPresent) {
        return popularMaterialsFlashSalesFirst;
      }
      const flashSaleInStockAndPopularMaterials = [...(flashSaleMaterials || []), ...inStockOptions.filter(inStockVal => !(flashSaleVariation[upholsteryOptionId] || []).includes(inStockVal.materialId)), ...popularMaterialsFlashSalesFirst.filter(val_0 => !instockOptionValueIds.includes(val_0.materialId) && !(flashSaleVariation[upholsteryOptionId] || []).includes(val_0.materialId))];
      return trimPopularMaterialsArray(flashSaleInStockAndPopularMaterials);
    }
    return popularMaterialsFlashSalesFirst;
  }, [flashSaleMaterials, flashSaleVariation, inStockOptions, instockOptionValueIds, popularMaterialsFlashSalesFirst, selectedOptionsFromURLOverride, upholsteryOptionId]);

  // Derive final popular materials array
  const popularMaterials = useMemo(() => {
    const initialMaterialFirst = moveMaterialToStart(popularMaterialsFlashSalesAndInStockFirst, !activeNamedFilters.length && !isReadyToShipTab ? initialSelectedMaterialRef.current : null);
    const trimmedInitialMaterialFirst = trimPopularMaterialsArray(initialMaterialFirst);
    return trimmedInitialMaterialFirst;
  }, [activeNamedFilters, isReadyToShipTab, popularMaterialsFlashSalesAndInStockFirst]);
  const namedFilterConditions = useMemo(() => ({
    [PDP_NAMED_FILTERS.FAVORITES]: material_3 => favoriteMaterials.includes(material_3),
    [PDP_NAMED_FILTERS.VELVET]: material_4 => !!material_4.velvet,
    [PDP_NAMED_FILTERS.GREENGUARD]: material_5 => !!material_5.greenguard_fabric,
    [PDP_NAMED_FILTERS.PETFRIENDLY]: material_6 => !!material_6.pet_friendly,
    [PDP_NAMED_FILTERS.PANTONE]: material_7 => !!material_7.pantone_flag,
    [PDP_NAMED_FILTERS.SAFEGUARD]: material_8 => !!material_8.performance_fabric,
    [PDP_NAMED_FILTERS.SUSTAINABLE]: material_9 => !!material_9.sustainable_fabric,
    [PDP_NAMED_FILTERS.CLEARANCE]: material_10 => clearanceFabricsSKU.includes(material_10.sku)
  }), [clearanceFabricsSKU, favoriteMaterials]);
  const isLeatherProduct = upholsteryOptionTypeName === 'Leather';
  const enableNamedFilters = !isReadyToShipTab && !isLeatherProduct;
  const enableColorFilters = !isLeatherProduct;

  // update filtered materials
  const filteredMaterials = useMemo(() => {
    const isInitialMaterialInAllMaterials = !!allMaterialsForCurrentTab.find(material_11 => material_11.materialId === initialSelectedMaterialRef.current?.materialId);

    // If the material is leather, return all
    if (isLeatherProduct) {
      return isInitialMaterialInAllMaterials ? getUniqueMaterials([initialSelectedMaterialRef.current, ...allMaterialsForCurrentTab]) : allMaterialsForCurrentTab;
    }
    const initialPopularAndAllMaterials = getUniqueMaterials([isInitialMaterialInAllMaterials && !activeNamedFilters.length ? initialSelectedMaterialRef.current : null, ...popularMaterials, ...allMaterialsForCurrentTab]);
    return initialPopularAndAllMaterials.filter(material_12 => {
      if (enableColorFilters && activeColorFilters.length && (!material_12.color || !activeColorFilters.includes(material_12.color.toLowerCase()))) {
        return false;
      }
      if (enableNamedFilters && activeNamedFilters.length && !activeNamedFilters.every(namedFilter => namedFilterConditions[namedFilter]?.(material_12))) {
        return false;
      }
      return true;
    });
  }, [activeColorFilters, activeNamedFilters, allMaterialsForCurrentTab, enableColorFilters, enableNamedFilters, isLeatherProduct, namedFilterConditions, popularMaterials]);

  // derive disabled materials
  const disabledMaterialIds = useMemo(() => {
    const {
      stockOptionsDisabledStatus
    } = stock || {};
    return isReadyToShipTab && allMaterialsForCurrentTab ? allMaterialsForCurrentTab.filter(material_13 => stockOptionsDisabledStatus?.[material_13.materialId]).map(material_14 => material_14.materialId) : [];
  }, [allMaterialsForCurrentTab, isReadyToShipTab, stock]);
  const filterCounts = useMemo(() => ({
    [PDP_NAMED_FILTERS.PETFRIENDLY]: allMaterialsForCurrentTab?.filter(material_15 => material_15.pet_friendly)?.length || 0,
    [PDP_NAMED_FILTERS.PANTONE]: allMaterialsForCurrentTab?.filter(material_16 => material_16.pantone_flag)?.length || 0,
    [PDP_NAMED_FILTERS.SAFEGUARD]: allMaterialsForCurrentTab?.filter(material_17 => material_17.performance_fabric)?.length || 0,
    [PDP_NAMED_FILTERS.SUSTAINABLE]: allMaterialsForCurrentTab?.filter(material_18 => material_18.sustainable_fabric)?.length || 0,
    [PDP_NAMED_FILTERS.VELVET]: allMaterialsForCurrentTab?.filter(material_19 => material_19.velvet)?.length || 0,
    [PDP_NAMED_FILTERS.FAVORITES]: Object.keys(favMaterialsObj || {}).length || 0,
    [PDP_NAMED_FILTERS.GREENGUARD]: allMaterialsForCurrentTab?.filter(material_20 => material_20.groupMaterial === 'greenguard' || GREENGUARD_FABRICS.includes(material_20.groupName))?.length || 0
  }), [allMaterialsForCurrentTab, favMaterialsObj]);
  const flashSaleOptionIds = useMemo(() => flashSaleMaterials.map(material_21 => material_21.materialId), [flashSaleMaterials]);
  return {
    data: {
      activeColorFilters,
      activeNamedFilters,
      allMaterials: allMaterialsForCurrentTab,
      allMaterialsLength: allMaterialsForCurrentTab.length,
      disabledMaterialIds,
      favMaterials: favMaterialsObj,
      filterCounts,
      filteredMaterials,
      flashSaleOptionIds,
      isClearanceFabricAvailable: clearanceFabricsSKU?.length > 0,
      isSelectedOptionsQuickship: stock?.isSelectedOptionsQuickship,
      optionName: upholsteryOptionTypeName,
      selectedMaterial,
      selectedOptionsStockQty: stock?.selectedOptionsStockQty,
      selectionValidity: stock?.selectionValidity,
      upholsteryOptionId
    },
    actions: {
      clearAllFilters,
      toggleColorFilter,
      toggleNamedFilter
    }
  };
};
export default usePDPFabricFilters;